'use client'

import React from 'react';
import { Helmet } from "react-helmet-async";

/**
 * AdDisclaimer Component
 * SEBI-compliance disclaimer — rebuilt with a real heading hierarchy
 * (h1 -> h2 -> h3) and WCAG AA-checked color contrast throughout.
 *
 * Palette (contrast checked against #FBFBF9 / #FFFFFF backgrounds):
 *   --ink      #0B2545   ~15:1  headings, primary text
 *   --slate    #3E4C59   ~9:1   secondary/body text
 *   --brand    #0B4A73   ~7.6:1 accents, icons, borders
 *   --brand-lt #EAF2F8          tint backgrounds only (never text-on-tint)
 *   --alert    #B42318   ~6:1   warnings (paired with icon, not color alone)
 *   --alert-lt #FDECEA          warning tint backgrounds
 *   --line     #E2E5E9          hairline dividers
 */
export default function AdDisclaimer() {
  const WarnIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );

  const RupeeIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M6 3h12M6 8h12M6 8a6 4 0 0 1 0 8h9M6 16l9 5" />
    </svg>
  );

  const ScopeIcon = (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );

  const clauses = [
    { n: '01', label: 'Risk Warning' },
    { n: '02', label: 'Prohibited Channels' },
    { n: '03', label: 'Financial Transparency' },
    { n: '04', label: 'Scope of Service' },
  ];

  return (
    <div className="min-h-screen bg-[#FBFBF9] text-[#3E4C59] font-sans selection:bg-[#0B4A73]/20">
    <Helmet>
  <title>AdDisclaimer | Option Bricks</title>
 
</Helmet>

      {/* subtle texture, kept quiet on purpose — this is a compliance page, not a hero */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(11,37,69,0.05)_1px,transparent_1px)] bg-[size:26px_26px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_45%,transparent_100%)]" />
      </div>

      <main className="relative z-10 w-full px-4 sm:px-8 md:px-12 py-16 md:py-20">
        <article className="relative w-full bg-white border border-[#E2E5E9] rounded-[1.75rem] overflow-hidden shadow-[0_1px_2px_rgba(11,37,69,0.06),0_12px_32px_-16px_rgba(11,37,69,0.18)]">

          <div className="h-1.5 w-full bg-[#0B4A73]" />

          {/* ===== Header ===== */}
          <header className="relative p-8 md:p-12 border-b border-[#E2E5E9]">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#0B4A73] text-white shadow-[0_10px_25px_-5px_rgba(11,74,115,0.45)]">
                <WarnIcon className="w-8 h-8" aria-hidden="true" />
              </div>
              <div>
                <span className="inline-block px-3 py-1 mb-3 rounded-full bg-[#0B4A73] text-white text-[11px] font-bold uppercase tracking-[0.16em]">
                  SEBI Compliance
                </span>
                <h1 className="text-3xl md:text-4xl font-extrabold text-[#0B2545] tracking-tight leading-tight mb-1.5">
                  Important Disclaimer
                </h1>
                <p className="text-[#0B4A73] font-semibold uppercase tracking-[0.14em] text-xs">
                  Read carefully before proceeding
                </p>
              </div>
            </div>

            {/* Clause index — orients the reader to the four sections below */}
            <ol className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              {clauses.map((c) => (
                <li key={c.n} className="flex items-center gap-2 text-xs font-semibold text-[#3E4C59]">
                  <span className="text-[#0B4A73] font-bold">{c.n}</span> {c.label}
                </li>
              ))}
            </ol>
          </header>

          {/* ===== Content ===== */}
          <div className="p-8 md:p-12 space-y-12 text-base md:text-lg leading-relaxed">

            {/* 01 — Risk Warning */}
            <section aria-labelledby="clause-01">
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-sm font-bold text-[#0B4A73]">01</span>
                <h2 id="clause-01" className="text-xl md:text-2xl font-bold text-[#0B2545] tracking-tight">
                  Risk Warning
                </h2>
              </div>
              <div className="space-y-4 pl-0 md:pl-8">
                <p>
                  Investing in the stock market carries inherent risks. We
                  <strong className="text-[#0B2545] font-bold"> do not promise guaranteed profits</strong> or fixed returns.
                  Trading involves the potential for both gains and losses, including the complete loss of your initial investment.
                </p>
                <p>
                  Our research service offers trading ideas with specified targets and stop losses, but the
                  <strong className="text-[#0B2545] font-semibold"> final decision to execute rests solely with you</strong>.
                  Leverage can magnify both gains and losses — make sure you understand the volatility involved before trading.
                </p>
              </div>
            </section>

            {/* 02 — Prohibited Channels */}
            <section aria-labelledby="clause-02" className="rounded-2xl bg-[#FDECEA] border border-[#F3C9C4] p-6 md:p-8">
              <div className="flex items-start gap-3">
                <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#B42318] text-white">
                  <WarnIcon className="w-4 h-4" aria-hidden="true" />
                </span>
                <div>
                  <div className="flex items-baseline gap-3 mb-1.5">
                    <span className="text-sm font-bold text-[#B42318]">02</span>
                    <h2 id="clause-02" className="text-lg md:text-xl font-bold text-[#0B2545] tracking-tight">
                      Prohibited Channels
                    </h2>
                  </div>
                  <p className="text-[#3E4C59]">
                    We <strong className="text-[#B42318] font-bold">do not</strong> offer recommendations or services through external platforms such as Telegram or Instagram.
                    Avoid any unauthorized channel claiming to represent us, and report it if you encounter one.
                  </p>
                </div>
              </div>
            </section>

            {/* 03 — Financial Transparency */}
            <section aria-labelledby="clause-03">
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-sm font-bold text-[#0B4A73]">03</span>
                <h2 id="clause-03" className="text-xl md:text-2xl font-bold text-[#0B2545] tracking-tight">
                  Financial Transparency
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:gap-8 gap-5 pl-0 md:pl-8">
                <div className="rounded-2xl bg-[#EAF2F8] border border-[#CFE1EC] p-6">
                  <div className="flex items-center gap-2.5 mb-2">
                    <RupeeIcon className="w-5 h-5 text-[#0B4A73]" aria-hidden="true" />
                    <h3 className="font-bold text-[#0B2545] text-base">Where to pay</h3>
                  </div>
                  <p className="text-sm md:text-base">
                    Payments should be made to <strong className="text-[#0B2545] font-semibold">Optionbricks' current account only</strong>.
                  </p>
                </div>

                <div className="rounded-2xl bg-[#FDECEA] border border-[#F3C9C4] p-6">
                  <div className="flex items-center gap-2.5 mb-2">
                    <WarnIcon className="w-5 h-5 text-[#B42318]" aria-hidden="true" />
                    <h3 className="font-bold text-[#0B2545] text-base">What we never accept</h3>
                  </div>
                  <p className="text-sm md:text-base">
                    We <strong className="text-[#B42318] font-bold">do not accept payments to personal accounts</strong>.
                    Any request for a personal transfer is unauthorized — report it immediately.
                  </p>
                </div>
              </div>
            </section>

            {/* 04 — Scope of Service */}
            <section aria-labelledby="clause-04" className="pt-8 border-t border-[#E2E5E9]">
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-sm font-bold text-[#0B4A73]">04</span>
                <h2 id="clause-04" className="text-xl md:text-2xl font-bold text-[#0B2545] tracking-tight">
                  Scope of Service
                </h2>
              </div>
              <div className="pl-0 md:pl-8">
                <p className="mb-6">
                  Our service provides information, guidance, and research to help clients make their own informed trading decisions.
                  It is <strong className="text-[#0B2545] font-semibold">not personalized investment advice</strong>, and we do not offer trade execution services.
                </p>

                {/* <h3 className="text-sm font-bold text-[#0B2545] mb-3 flex items-center gap-2">
                  <ScopeIcon className="w-4 h-4 text-[#0B4A73]" aria-hidden="true" />
                  Related policies
                </h3>
                <nav aria-label="Related policies">
                  <ul className="flex flex-wrap gap-3">
                    {['Disclaimer', 'Legal Terms', 'Refund Policy', 'Privacy Policy'].map((item) => (
                      <li key={item}>
                        <a
                          href="#"
                          className="inline-block px-4 py-2 rounded-full bg-white border border-[#E2E5E9] text-sm font-semibold text-[#0B2545] hover:text-white hover:bg-[#0B4A73] hover:border-[#0B4A73] transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B4A73] focus-visible:ring-offset-2"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav> */}
             
             
              </div>
            </section>
          </div>

          {/* ===== Footer ===== */}
          <footer className="bg-[#F5F6F7] p-6 text-center border-t border-[#E2E5E9]">
            <p className="text-[#3E4C59] text-xs uppercase tracking-[0.35em] font-semibold">
              Security • Research • Integrity
            </p>
          </footer>
        </article>
      </main>
    </div>
  );
}